import Card from 'ui/components/atoms/Card';
import Tag from 'ui/components/atoms/Tag';
import { dateFormatter } from 'ui/helpers/utils';
import eventClickNews from 'ui/lib/gtm/eventClickNews';
import useIsElementVisible from 'ui/hooks/useIsElementVisible';
import useIsNativeView from '../../../hooks/useIsNativeView';
import generateImageResizer from '../../../helpers/utils/generateImageResizer';
import { useRouter } from 'next/router';

declare const window: WindowTypes;

interface IOtherArticlesCardProps {
  categoryGTM: string;
  handleCollectDataLayer: (data: {
    id: string;
    name: string;
    creative: string;
    position: string;
  }) => void;
  classNames?: string;
  enableSharing?: boolean;
  optimize?: boolean;
  data: {
    id: string;
    titlePage: string;
    slug: string;
    slugUnique?: string;
    contentCategory: {
      name: string;
    };
    tag: {
      tagName: string;
    };
    heroImageLink: string;
    altImage: string;
    publishedDate: string;
  };
  idx: number;
}

const OtherArticlesCard = ({
  categoryGTM,
  handleCollectDataLayer = () => {},
  classNames = 'h-full border-b border-reliableBlack70 snap-center',
  enableSharing = false,
  optimize,
  data,
  idx,
}: IOtherArticlesCardProps) => {
  const router = useRouter();
  const isNativeView = useIsNativeView();
  const slugUniqueSplitted = data?.slugUnique ? data?.slugUnique.split('/') : '';
  const dataSlug = slugUniqueSplitted[slugUniqueSplitted.length - 1] || '';

  const ref = useIsElementVisible(
    () =>
      handleCollectDataLayer({
        id: data?.id,
        name: data?.titlePage?.toLowerCase() || 'tidak tersedia',
        creative: `/berita-dan-tips/${dataSlug?.toLowerCase()}`,
        position: `${idx + 1} - berita & tips ${data?.contentCategory?.name?.toLowerCase()}`,
      }),
    [data]
  );

  const handleGoToNewsTipsDetail = (e, slug) => {
    if (isNativeView) {
      e.preventDefault();

      if (typeof window !== 'undefined') {
        window?.androidApp?.goToNewsTipsDetail(slug);
        window?.webkit?.messageHandlers?.iosApp?.postMessage(`goToNewsTipsDetail/${slug}`);
      }
    }
  };

  return (
    <Card ref={ref} key={`${dataSlug}-${idx}`} className={classNames}>
      {data.tag && (
        <Tag className="rounded-br lg:hidden" severity="warn">
          {data.tag.tagName}
        </Tag>
      )}
      <div className="flex flex-col gap-4 pb-4 lg:flex-col lg:pt-0 lg:px-0 lg:pb-0">
        <a
          id={`card-dtl-news-${dataSlug}`}
          href={enableSharing ? null : `${data?.slug}`}
          role={enableSharing ? null : 'link'}
          onClick={(e) => {
            eventClickNews({
              id: data?.id,
              title: data?.titlePage,
              category: data?.contentCategory?.name?.toLowerCase(),
              slug: dataSlug,
              index: idx + 1,
              anotherCategory: categoryGTM || `artikel lainnya`,
              baseSlug: router.basePath,
            });

            handleGoToNewsTipsDetail(e, dataSlug);
          }}
        >
          <div className="flex-auto">
            {data?.heroImageLink ? (
              <img
                src={
                  optimize
                    ? generateImageResizer(data?.heroImageLink, 384, 'webp')
                    : data.heroImageLink
                }
                alt={data.altImage}
                width={500}
                height={500}
                loading="lazy"
                className="w-full aspect-video object-cover mb-4"
              />
            ) : null}
          </div>
          <div className="h-10 text-ellipsis overflow-hidden lg:h-14">
            <p className="text-sm text-start font-semibold text-reliableBlack line-clamp-2 lg:text-lg lg:font-bold">
              {data.titlePage}
            </p>
          </div>
          <p className="text-xs font-normal text-reliableBlack70 lg:mb-4 lg:text-sm lg:text-reliableBlack">
            {dateFormatter(data.publishedDate, enableSharing) || null}
          </p>
        </a>
      </div>
    </Card>
  );
};

export default OtherArticlesCard;
